import type { CSSObject } from '@emotion/react';
import { modalClasses } from './modal.classes';
import { type ModalVerticalPosition } from './modal';
import { useTheme } from '@doxyme/ui-foundation/theme';
import { useViewport } from '@doxyme/ui-foundation/hooks/useViewport';

type Props = {
  verticalPosition?: ModalVerticalPosition;
};

type Return = Record<'root' | 'backdrop', CSSObject>;

export const useModalStyles = ({ verticalPosition }: Props = {}): Return => {
  const theme = useTheme();
  const { isSmallViewport, isMediumViewport, isLargeViewport } = useViewport();
  const isSmallViewportWithoutPositioning =
    isSmallViewport && !Boolean(verticalPosition);

  const commonContainerStyles: CSSObject = {
    display: 'flex',
    flexDirection: 'column',
    width: '480px',
    background: theme.modal.background,
    borderRadius: theme.shape.radius12,
    outline: `1px solid ${theme.modal.border}`,
    overflow: 'hidden',
    animation: `scale-up ${theme.transitions.duration.shorter}ms ${theme.transitions.easing.easeOut}`,
    '@keyframes scale-up': {
      from: {
        transform: 'scale(0.75)',
      },
      to: {
        transform: 'scale(1)',
      },
    },
  };

  return {
    root: {
      position: 'fixed',
      inset: 0,
      zIndex: theme.zIndex.modal,

      ...(isSmallViewportWithoutPositioning && {
        [`.${modalClasses.container}`]: {
          maxHeight: `calc(100% - ${theme.spacing(3)})`,
          background: theme.modal.background,
          borderTopLeftRadius: theme.shape.radius12,
          borderTopRightRadius: theme.shape.radius12,
          border: `1px solid ${theme.modal.border}`,
          overflow: 'hidden',
          outline: 'none',
        },
      }),

      ...(!isSmallViewportWithoutPositioning && {
        display: 'flex',
        alignItems: verticalPosition ?? 'center',
        justifyContent: 'center',
        margin: theme.spacing(3),

        [`.${modalClasses.container}`]: {
          ...commonContainerStyles,
          maxHeight: `calc(100% - ${theme.spacing(3)})`,
        },
      }),

      ...((isMediumViewport || isLargeViewport) && {
        [`.${modalClasses.container}`]: {
          ...commonContainerStyles,
          maxHeight: '90%',
        },
      }),
    },
    backdrop: {
      backgroundColor: theme.background.dimmingLayer,
    },
  };
};
